/**
 * AI BOARD PRESENTATION GENERATOR LOGIC
 * Transformerer komplekse målinger til strategisk styrerapportering.
 */

export const generateBoardPresentation = (input) => {
  const { metrics, companyName, period, strengths, risks, recommendations } = input;
  const { we, ohs, cri, stress, psi, leadership, culture } = metrics;

  return {
    meta: {
      company: companyName,
      period: period || "Q2 2024",
      title: "Strategisk Rapport: Arbeidsmiljø & Org. Helse"
    },
    slides: [
      {
        id: 1,
        title: "Executive Summary",
        content: `Organisasjonen viser en OHS på ${ohs}%, noe som indikerer ${ohs > 60 ? 'stabil drift med selektive utfordringer' : 'behov for strukturelle grep'}. Hovedfokus dette kvartalet er korrelasjonen mellom høyt arbeidspress (${stress}%) og fallende psykologisk trygghet (${psi}%).`,
        talkingPoints: [
          "OHS er vår ledende indikator for fremtidig operativ evne.",
          "Vi ser en trend hvor kortsiktig leveranseevne går på bekostning av langsiktig kulturkapital.",
          "Styret bør merke seg at stressnivået nå påvirker innovasjonstakten."
        ]
      },
      {
        id: 2,
        title: "Nøkkeltall & Indekser",
        metrics: [
          { label: "Work Environment Score", val: we, status: we > 60 ? 'Stable' : 'At Risk' },
          { label: "Conflict Risk Index", val: cri, status: cri < 40 ? 'Low' : 'High' },
          { label: "Stress Index", val: stress, status: stress < 60 ? 'Managed' : 'Critical' },
          { label: "Leadership Score", val: leadership, status: leadership > 70 ? 'Strong' : 'Weak' }
        ],
        talkingPoints: [
          "CRI på ${cri}% er en økning fra forrige periode, drevet av uklarhet i beslutningslinjer.",
          "Leadership Score reflekterer et gap i mellomleders støttekapasitet under press."
        ]
      },
      {
        id: 3,
        title: "Strategisk Risiko",
        risks: risks.map(r => ({
          title: r,
          impact: "Svekket retention av nøkkelpersonell og økt risiko for feilproduksjon."
        })),
        talkingPoints: [
          "Den største risikoen er ikke enkeltstående konflikter, men systemisk utmatting i tekniske team.",
          "Dersom PSI faller under 40%, risikerer vi en taushetskultur som skjuler operative avvik."
        ]
      },
      {
        id: 4,
        title: "Beslutningspunkter & Veien Videre",
        actions: recommendations,
        governance: [
          "Godkjenning av budsjett for Lederstøtte-program (Fase 1).",
          "Integrering av Org. Helse som fast KPI i månedlig rapportering.",
          "Revisjon av ressursallokeringsmodell for å adressere stress-topper."
        ],
        talkingPoints: [
          "Vi ber om styrets støtte til å prioritere kulturbygging over ytterligere kapasitetsøkning i Q3.",
          "Tiltakene er designet for å gi ROI i form av redusert sykefravær (-10%) innen 6 måneder."
        ]
      }
    ],
    narrative: `Denne rapporten sammenstiller data fra ${companyName} for perioden ${period}. Den overordnede konklusjonen er at virksomheten har en solid kjerne, men at den operative modellen utfordres av et vedvarende høyt stressnivå. Ledelsens evne til å modellere psykologisk trygghet vil være avgjørende for å opprettholde konkurransekraft i et stramt arbeidsmarked.`
  };
};

export const mockBoardInput = {
  companyName: "TechCorp AS",
  period: "Q2 2024",
  metrics: {
    we: 58,
    ohs: 54,
    cri: 65,
    stress: 78,
    psi: 42,
    leadership: 48,
    culture: 52
  },
  strengths: ["Faglig engasjement", "Operativt samarbeid", "Stolthet til produkt"],
  risks: ["Systemisk utbrenthet", "Fallende tillit til kommunikasjon", "Taushetskultur"],
  recommendations: [
    "Implementere 90-dagers forbedringsplan",
    "Lansere Lederstøtte-program",
    "Gjennomføre prioriterings-vask i IT-avdelingen"
  ]
};